<template>
  <main class="grow alt">
    <!-- HERO (dark band, blog-editorial idiom) -->
    <header class="alt-hero">
      <div class="alt-shell">
        <nav class="alt-crumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/')">Home</NuxtLink>
          <span aria-hidden="true">/</span>
          <NuxtLink :to="localePath('/alternatives')">Alternatives</NuxtLink>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{{ page.eyebrow }}</span>
        </nav>
        <h1>{{ page.h1 }}</h1>
        <p v-if="page.answerSummary" class="alt-answer">{{ page.answerSummary }}</p>
        <p class="alt-lede">{{ page.lede }}</p>
        <div class="alt-byline">
          <Logo class="alt-byline-logo" aria-hidden="true" />
          <div>
            <p class="alt-byline-name">{{ page.author.name }}, {{ page.author.role }}</p>
            <p class="alt-byline-dates">
              Published
              <time :datetime="page.publishedIso">{{ page.published }}</time>
              · Updated
              <time :datetime="page.updatedIso">{{ page.updated }}</time>
            </p>
          </div>
        </div>
        <p class="alt-methodology"><span>Methodology —</span> {{ page.methodology }}</p>
        <CompetitorNotice :competitor="page.competitor" :date="page.updated" :iso="page.updatedIso" on-dark />
        <!-- The trial terms follow the trial button in the markup, so up to 767 px they sit directly under it (legal B1.1);
             from 768 px the button and Compare plans share a row and the terms take their own line under it. -->
        <div class="alt-cta-row alt-hero-ctas">
          <a
            :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
            class="alt-btn"
            data-conversion-event="start_free_click"
            :data-conversion-label="`${page.h1} hero CTA (Voyager)`"
            :data-conversion-location="location"
            :data-conversion-variant="ctaVariant || undefined"
            :style="ctaEnrolled ? { minWidth: 'min(18em, 100%)' } : null"
          >{{ ctaEnrolled ? ctaLabel : 'Start free with Voyager — SMS built in' }}</a>
          <div class="alt-hero-terms"><TrialDisclosure plan="voyager" on-dark /></div>
          <NuxtLink
            :to="localePath('/pricing')"
            class="alt-hero-link"
            data-conversion-event="pricing_click"
            :data-conversion-label="`${page.h1} hero pricing link`"
            :data-conversion-location="location"
          >Compare plans →</NuxtLink>
        </div>
        <ChatGptProof :location="location" on-dark />
      </div>
    </header>

    <div class="alt-body">
      <!-- QUICK ANSWER -->
      <section class="alt-col alt-section" aria-labelledby="alt-quick-title">
        <h2 id="alt-quick-title">{{ page.quickTitle || 'The short version, by scenario' }}</h2>
        <div class="alt-quick">
          <p v-for="(paragraph, i) in page.quickAnswer" :key="i">
            <template v-if="typeof paragraph === 'string'">{{ paragraph }}</template>
            <template v-else-if="paragraph.parts">
              <template v-for="(part, j) in paragraph.parts" :key="j">
                <template v-if="typeof part === 'string'">{{ part }}</template>
                <NuxtLink v-else :to="localePath(part.path)">{{ part.label }}</NuxtLink>
              </template>
            </template>
            <template v-else>{{ paragraph.text }}<NuxtLink :to="localePath(paragraph.link.path)">{{ paragraph.link.label }}</NuxtLink>{{ paragraph.after }}</template>
          </p>
        </div>
      </section>

      <!-- SETUP AND MIGRATION OFFER (pages that set setupOfferEarly; hides itself when the offer ends) -->
      <SwitchSave v-if="page.setupOfferEarly" :competitor-slug="page.slug" :location="location" />

      <!-- VIDEO (only where a comparison video exists: data/comparison-videos.ts) -->
      <ComparisonVideo :path="location" :canonical-url="canonicalUrl" :page-id="`${canonicalUrl}#webpage`" article />

      <!-- WHY PEOPLE LEAVE -->
      <section class="alt-col alt-section" aria-labelledby="alt-why-title">
        <h2 id="alt-why-title">Why people leave {{ page.competitor }} in 2026</h2>
        <p v-if="page.eventsIntro">{{ page.eventsIntro }}</p>
        <p v-else>
          Four dated changes in twenty months hit the same wallet. These are the events
          behind the current wave of “{{ page.competitor }} alternatives” searches:
        </p>
        <ol class="alt-events">
          <li v-for="event in page.events" :key="event.date">
            <span class="alt-event-date">{{ event.date }}</span>
            <div>
              <h3>{{ event.title }}</h3>
              <p>{{ event.body }}</p>
            </div>
          </li>
        </ol>
        <template v-if="visibleQuotes.length">
          <h3 class="alt-subhead">What {{ page.competitor }} users say</h3>
          <blockquote v-for="quote in visibleQuotes" :key="quote.text" class="alt-quote">
            <p>“{{ quote.text }}”</p>
            <footer>
              — {{ quote.attribution }} ·
              <a :href="quote.sourceUrl" target="_blank" rel="noopener noreferrer">{{ quote.sourceLabel }} ↗</a>
            </footer>
          </blockquote>
        </template>
      </section>

      <!-- PHONE AND IPAD -->
      <section v-if="page.mobileApp" class="alt-col alt-section" aria-labelledby="alt-mobile-title">
        <h2 id="alt-mobile-title">{{ page.competitor }} on a phone or iPad</h2>
        <p>{{ page.mobileApp.intro }}</p>
        <blockquote v-for="quote in page.mobileApp.quotes" :key="quote.text" class="alt-quote">
          <p>“{{ quote.text }}”</p>
          <footer>
            — {{ quote.attribution }} ·
            <a :href="quote.sourceUrl" target="_blank" rel="noopener noreferrer">{{ quote.sourceLabel }} ↗</a>
          </footer>
        </blockquote>
        <h3 class="alt-subhead">Workspace369 on iPhone and iPad</h3>
        <ul>
          <li v-for="point in workspaceMobileApp.points" :key="point">{{ point }}</li>
        </ul>
        <p>
          App Store listing, checked {{ workspaceMobileApp.store.checkedOn }}:
          <template v-for="row in workspaceMobileApp.store.rows" :key="row.device">{{ ' ' }}{{ row.device }}: {{ row.requirement }}</template>{{ ' ' }}
          <a :href="workspaceMobileApp.store.url" target="_blank" rel="noopener noreferrer">{{ workspaceMobileApp.store.label }} ↗</a>
        </p>
        <p>
          Quotes are from public App Store reviews of {{ page.competitor }}’s app, quoted as written. Each is one person’s experience, and the app may have changed since.
          <NuxtLink :to="localePath(workspaceMobileApp.featurePath)" class="alt-inline-cta">See the Workspace369 mobile app →</NuxtLink>
        </p>
      </section>

      <!-- PHONE AND IPAD, Workspace369 side only (pages in data/mobile-app-facts.ts mobileProofPaths) -->
      <section v-if="!page.mobileApp && mobileProofPaths.has(location)" class="alt-col alt-section" aria-labelledby="alt-mobile-title">
        <h2 id="alt-mobile-title">Workspace369 on iPhone and iPad</h2>
        <MobileAppProof :location="location" />
      </section>

      <!-- COMPARISON TABLE -->
      <section class="alt-wide alt-section" aria-labelledby="alt-table-title">
        <div class="alt-col alt-section-head">
          <h2 id="alt-table-title">{{ page.competitor }} alternatives compared</h2>
          <p>
            The two columns that decide most switches are SMS and voice, and built-in
            accounting. {{ page.competitor }} is the
            first row for context; the alternatives follow.
          </p>
        </div>
        <div class="alt-table-shell" tabindex="0" role="region" aria-labelledby="alt-table-title">
          <table class="alt-table">
            <thead>
              <tr>
                <th scope="col" class="alt-col-tool">Tool</th>
                <th v-for="column in columns" :key="column.key" scope="col">{{ column.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="tool in page.tools"
                :key="tool.name"
                :class="{ 'alt-row-us': tool.isUs, 'alt-row-baseline': tool.baseline }"
              >
                <th scope="row" class="alt-col-tool">
                  {{ tool.name }}
                  <span v-if="tool.baseline" class="alt-tag">current tool</span>
                  <span v-if="tool.isUs" class="alt-tag alt-tag-us">ours</span>
                  <a
                    v-if="isExternal(tool.pricingUrl)"
                    :href="tool.pricingUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="alt-tool-pricing-link"
                  >pricing ↗</a>
                  <NuxtLink v-else :to="localePath(tool.pricingUrl)" class="alt-tool-pricing-link">pricing →</NuxtLink>
                </th>
                <td v-for="column in columns" :key="column.key">
                  <template v-if="tool[column.key] === 'yes'">
                    <span class="alt-check" aria-hidden="true">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m4 8.4 2.6 2.6L12 5.6" /></svg>
                    </span>
                    <span class="sr-only">Included</span>
                  </template>
                  <template v-else-if="tool[column.key] === 'no'">
                    <!-- A competitor's dash is a dated claim about its own pages, so it links to the page we read. -->
                    <a
                      v-if="!tool.isUs && isExternal(tool.pricingUrl)"
                      :href="tool.pricingUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="alt-dash-link"
                      :title="notListedLabel(tool)"
                    >
                      <span class="alt-dash" aria-hidden="true">—</span>
                      <span class="sr-only">{{ notListedLabel(tool) }}</span>
                    </a>
                    <template v-else>
                      <span class="alt-dash" aria-hidden="true">—</span>
                      <span class="sr-only">Not included</span>
                    </template>
                  </template>
                  <template v-else>{{ tool[column.key] }}</template>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="page.photographerNote">
              <tr>
                <td :colspan="columns.length + 1" class="alt-table-note-row">{{ page.photographerNote }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p class="alt-col alt-table-note">{{ page.tableNote }}</p>
        <p class="alt-col alt-table-note" data-dash-key>
          A dash on another tool's row means the feature is not listed on that tool's pricing page on the date shown for
          that row. It links to the page we read, so you can check it yourself.
        </p>
      </section>

      <!-- PER-TOOL SECTIONS -->
      <section class="alt-col alt-section" aria-labelledby="alt-tools-title">
        <h2 id="alt-tools-title">The alternatives, in detail</h2>
        <article v-for="tool in alternativeTools" :key="tool.name" class="alt-tool" :aria-labelledby="`alt-tool-${slugify(tool.name)}`">
          <h3 :id="`alt-tool-${slugify(tool.name)}`">
            {{ tool.name }}
            <a :href="tool.officialUrl" target="_blank" rel="noopener noreferrer" class="alt-tool-site">{{ hostOf(tool.officialUrl) }} ↗</a>
          </h3>
          <p class="alt-tool-best"><strong>Best for:</strong> {{ tool.bestFor }}</p>
          <p><strong>Pricing:</strong> {{ tool.pricingDetail }}</p>
          <div class="alt-tool-grid">
            <div>
              <p class="alt-tool-label">Where it wins</p>
              <ul>
                <li v-for="item in tool.strengths" :key="item">{{ item }}</li>
              </ul>
            </div>
            <div>
              <p class="alt-tool-label">Real limitations</p>
              <ul>
                <li v-for="item in tool.limitations" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
          <blockquote v-if="tool.quote && !tool.quote.needsReview" class="alt-quote">
            <p>“{{ tool.quote.text }}”</p>
            <footer>
              — {{ tool.quote.attribution }} ·
              <a :href="tool.quote.sourceUrl" target="_blank" rel="noopener noreferrer">source ↗</a>
            </footer>
          </blockquote>
          <p class="alt-tool-skip"><strong>Skip it if:</strong> {{ tool.skipIf }}</p>
          <!-- Each trial link carries its own plan's full trial line directly under it (legal B1.1); pricing links follow. -->
          <div v-if="tool.ctas?.length" class="alt-tool-ctas">
            <div v-for="cta in tool.ctas.filter((c) => c.href)" :key="cta.label" class="alt-trial">
              <a
                :href="signupUrl(cta.href)"
                class="alt-btn"
                :class="{ 'alt-btn-outline': cta.kind === 'secondary' }"
                data-conversion-event="start_free_click"
                :data-conversion-label="`${page.h1} — ${cta.label}`"
                :data-conversion-location="location"
              >{{ cta.label }}</a>
              <TrialDisclosure :plan="trialPlan(cta.href)" on-light />
            </div>
            <NuxtLink
              v-for="cta in tool.ctas.filter((c) => !c.href)"
              :key="cta.label"
              :to="localePath(cta.path)"
              class="alt-inline-cta"
              data-conversion-event="pricing_click"
              :data-conversion-label="`${page.h1} — ${cta.label}`"
              :data-conversion-location="location"
            >{{ cta.label }} →</NuxtLink>
          </div>
        </article>
      </section>

      <!-- STAY ON X IF -->
      <section class="alt-col alt-section" aria-labelledby="alt-stay-title">
        <h2 id="alt-stay-title">Stay on {{ page.competitor }} if…</h2>
        <ul class="alt-stay">
          <li v-for="item in page.stayIf" :key="item">{{ item }}</li>
        </ul>
      </section>

      <!-- HOW TO SWITCH -->
      <section class="alt-col alt-section" aria-labelledby="alt-switch-title">
        <h2 id="alt-switch-title">How to switch from {{ page.competitor }}</h2>
        <ol class="alt-steps">
          <li v-for="(step, i) in page.switchSteps" :key="step.title">
            <span class="alt-step-num">{{ String(i + 1).padStart(2, '0') }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.body }}</p>
            </div>
          </li>
        </ol>
        <p>{{ page.switchIntro }}</p>
        <p>
          <NuxtLink :to="localePath(page.switchLink.path)" class="alt-inline-cta">{{ page.switchLink.label }} →</NuxtLink>
        </p>
      </section>

      <!-- FAQ -->
      <section class="alt-col alt-section" aria-labelledby="alt-faq-title">
        <h2 id="alt-faq-title">Questions {{ page.competitor }} users actually ask</h2>
        <dl class="alt-faq">
          <div v-for="item in page.faqs" :key="item.q">
            <dt>{{ item.q }}</dt>
            <dd>{{ item.a }}</dd>
          </div>
        </dl>
      </section>

      <!-- FINAL CTA -->
      <section class="alt-col alt-section" aria-labelledby="alt-final-title">
        <div class="alt-final">
          <Logo class="alt-final-logo" aria-hidden="true" />
          <h2 id="alt-final-title">Try the workspace with texting and calling inside.</h2>
          <p>
            Texts and calls start on Voyager ($74/mo, 2 seats); IVR, call queues and call
            recording on Specialist ($149/mo, 10 seats). 14-day free trial — bring one client and run the
            full loop.
          </p>
          <!-- Each trial button carries its own plan's full trial line directly under it (legal B1.1); See pricing follows. -->
          <div class="alt-cta-row alt-cta-center">
            <div class="alt-trial">
              <a
                :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
                class="alt-btn"
                data-conversion-event="start_free_click"
                :data-conversion-label="`${page.h1} final CTA (Voyager)`"
                :data-conversion-location="location"
                :data-conversion-variant="ctaVariant || undefined"
              >Start free with Voyager</a>
              <TrialDisclosure plan="voyager" centered on-light />
            </div>
            <div class="alt-trial">
              <a
                :href="signupUrl('https://app.workspace369.com/signup?plan=specialist')"
                class="alt-btn alt-btn-outline"
                data-conversion-event="start_free_click"
                :data-conversion-label="`${page.h1} final CTA (Specialist)`"
                :data-conversion-location="location"
              >Try Specialist for IVR and call queues</a>
              <TrialDisclosure plan="specialist" centered on-light />
            </div>
            <NuxtLink
              :to="localePath('/pricing')"
              class="alt-inline-cta"
              data-conversion-event="pricing_click"
              :data-conversion-label="`${page.h1} final pricing link`"
              :data-conversion-location="location"
            >See pricing →</NuxtLink>
          </div>
        </div>
      </section>

      <!-- INTERNAL LINKS -->
      <section class="alt-col alt-section" aria-labelledby="alt-related-title">
        <h2 id="alt-related-title">Related pages</h2>
        <ul class="alt-related">
          <li v-for="link in page.relatedLinks" :key="link.path">
            <NuxtLink :to="localePath(link.path)">
              <span>
                <strong>{{ link.label }}</strong>
                <small>{{ link.description }}</small>
              </span>
              <span aria-hidden="true" class="alt-related-arrow">→</span>
            </NuxtLink>
          </li>
        </ul>
      </section>

      <!-- SOURCES -->
      <section v-if="page.sources?.length" class="alt-col alt-section" aria-labelledby="alt-sources-title">
        <h2 id="alt-sources-title" class="alt-sources-title">Sources</h2>
        <ul class="alt-sources">
          <li v-for="source in page.sources" :key="source.url">
            <a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.label }} ↗</a>
          </li>
        </ul>
      </section>
    </div>

    <!-- Mobile sticky CTA -->
    <div class="alt-sticky" :class="{ 'is-off': overFooter }" :aria-hidden="overFooter ? 'true' : undefined" :inert="overFooter || undefined">
      <div>
        <strong>{{ page.competitor }} alternative with SMS + voice</strong>
        <TrialDisclosure compact plan="voyager" on-light />
      </div>
      <div class="alt-sticky-actions">
        <NuxtLink
          :to="localePath('/pricing')"
          class="alt-sticky-pricing"
          data-conversion-event="pricing_click"
          :data-conversion-label="`${page.h1} sticky pricing`"
          :data-conversion-location="location"
        >{{ localeUi.nav.pricing }}</NuxtLink>
      <a
        :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
        class="alt-sticky-btn"
        data-conversion-event="start_free_click"
        :data-conversion-label="`${page.h1} sticky CTA (Voyager)`"
        :data-conversion-location="location"
      >Try Voyager free</a>
      </div>
    </div>
  </main>
</template>

<script setup>
import Logo from '~/components/Logo.vue';
import { localizedUrl } from '~/data/locales';
import { mobileProofPaths, workspaceMobileApp } from '~/data/mobile-app-facts';

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
});

const { addReferralToUrl } = useReferral();
// Pages that set utmSource tag their sign-up links so the source survives without cookie consent.
const signupUrl = (href) => {
  const url = new URL(href);
  if (!props.page.utmSource || url.origin !== 'https://app.workspace369.com' || url.pathname !== '/signup') return addReferralToUrl(href);
  url.searchParams.set('utm_source', props.page.utmSource);
  url.searchParams.set('utm_medium', 'organic_page');
  return addReferralToUrl(url.toString());
};
// The plan a sign-up link opens, so its trial line quotes that plan's price.
const trialPlan = (href) => new URL(href).searchParams.get('plan') || '';
const { canonicalLocaleCode, localePath, localeUi } = useWorkspaceLocale();
// Q152: the bar sat over the site footer's links at the page bottom. Slide it away while the footer is on screen,
// as the compare bar does. Starts shown (SSR), so nothing shifts; it is position: fixed either way.
const overFooter = ref(false);
let footerObserver = null;
onMounted(() => {
  const footer = document.querySelector('footer.site-footer');
  if (!footer) return;
  footerObserver = new IntersectionObserver(([entry]) => { overFooter.value = entry.isIntersecting; });
  footerObserver.observe(footer);
});
onBeforeUnmount(() => footerObserver?.disconnect());


const location = computed(() => `/alternatives/${props.page.slug}`);
// The hero runs the CTA copy test on the mobile-proof pages (composables/useCtaTest.ts).
const { enrolled: ctaEnrolled, variant: ctaVariant, label: ctaLabel } = useCtaTest(location);
const canonicalUrl = computed(() => localizedUrl('https://workspace369.com', location.value, canonicalLocaleCode.value));

const columns = [
  { key: 'price', label: 'Price (tier most buyers need)' },
  { key: 'seats', label: 'Seats' },
  { key: 'sms', label: 'SMS' },
  { key: 'voice', label: 'Voice / phone' },
  { key: 'accounting', label: 'Built-in accounting' },
  { key: 'portal', label: 'Client portal' },
  { key: 'paymentPlans', label: 'Payment plans' },
  { key: 'timeTracking', label: 'Time tracking' },
  { key: 'checkedOn', label: 'Last checked' },
];

const alternativeTools = computed(() => props.page.tools.filter((tool) => !tool.baseline));
const visibleQuotes = computed(() => (props.page.quotes || []).filter((quote) => !quote.needsReview));

const isExternal = (url) => /^https?:\/\//.test(url);
const notListedLabel = (tool) =>
  `Not listed on ${tool.name}'s pricing page${tool.checkedOn ? ` (checked ${tool.checkedOn})` : ''}`;
const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};
const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
</script>

<style scoped>
/* ---------- Page frame ---------- */
.alt {
  background: #ffffff;
  color: #0f172a;
}

.alt-shell {
  width: min(100% - 2rem, 72rem);
  margin-inline: auto;
}

/* Prose column (blog-article width) vs wide blocks (tables) */
.alt-col {
  width: min(100% - 2rem, 46rem);
  margin-inline: auto;
}

.alt-wide {
  width: min(100% - 2rem, 72rem);
  margin-inline: auto;
}

/* ---------- Hero: dark split band, same idiom as the blog editorial hero ---------- */
.alt-hero {
  background: #020617;
  padding: 6.5rem 0 3.5rem;
}

@media (min-width: 900px) {
  .alt-hero {
    padding: 10rem 0 5rem;
  }
}

.alt-crumb {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 700;
}

.alt-crumb a {
  color: #94a3b8;
  text-decoration: none;
}

.alt-crumb a:hover {
  color: #67e8f9;
}

.alt-crumb span[aria-hidden] {
  color: #475569;
}

.alt-hero h1 {
  margin-top: 1.75rem;
  max-width: 46rem;
  color: #ffffff;
  font-size: clamp(2.1rem, 5vw, 3.5rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.05;
  text-wrap: balance;
}

/* Phones: the long listicle H1 ran 5-7 lines at 125% text and pushed the answer to the fold */
@media (max-width: 640px) {
  .alt-hero h1 {
    margin-top: 1rem;
    font-size: 1.625rem;
    line-height: 1.1;
  }
}

.alt-answer {
  margin-top: 1.25rem;
  max-width: 44rem;
  color: #ffffff;
  font-size: clamp(1.02rem, 1.9vw, 1.16rem);
  font-weight: 600;
  line-height: 1.65;
}

.alt-lede {
  margin-top: 1.25rem;
  max-width: 44rem;
  color: #cbd5e1;
  font-size: clamp(1rem, 1.8vw, 1.13rem);
  line-height: 1.7;
}

.alt-byline {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  margin-top: 1.5rem;
}

.alt-byline-logo {
  width: 2.4rem;
  height: 2.4rem;
  flex: none;
  border: 1px solid #1e293b;
  border-radius: 0.5rem;
  background: #07101f;
  color: #67e8f9;
  padding: 0.45rem;
}

.alt-byline p {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.55;
}

.alt-byline-name {
  color: #e2e8f0;
  font-weight: 750;
}

.alt-byline-dates {
  color: #94a3b8;
}

.alt-methodology {
  margin-top: 1.4rem;
  max-width: 44rem;
  color: #94a3b8;
  font-size: 0.8rem;
  line-height: 1.65;
}

.alt-methodology span {
  color: #cbd5e1;
  font-weight: 700;
}

.alt-cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem 1.2rem;
  margin-top: 1.8rem;
}

.alt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.9rem;
  border: 1px solid #0e7490;
  border-radius: 6px;
  background: #0e7490;
  color: #ffffff;
  padding: 0.72rem 1.45rem;
  font-size: 0.92rem;
  font-weight: 800;
  transition: background-color 150ms ease, border-color 150ms ease;
}

.alt-btn:hover {
  border-color: #155e75;
  background: #155e75;
}

.alt-btn-outline {
  border-color: #cbd5e1;
  background: transparent;
  color: #0f172a;
}

.alt-btn-outline:hover {
  border-color: #1ba7ce;
  background: #f0f9fc;
  color: #0e7490;
}

.alt-hero .alt-btn-outline {
  border-color: #334155;
  color: #e2e8f0;
}

.alt-hero .alt-btn-outline:hover {
  border-color: #67e8f9;
  background: transparent;
  color: #67e8f9;
}

/* Uncapped full-width wrapper so the terms always break onto their own line; the 34rem cap stays on the line. */
.alt-hero-terms {
  flex-basis: 100%;
  margin-top: -0.35rem;
}

.alt-hero-terms :deep(.trial-disclosure) {
  margin-top: 0;
}

@media (min-width: 768px) {
  .alt-hero-terms {
    order: 1;
  }
}

.alt-hero-link {
  /* 44px+ tap target on phones (was 28px). */
  display: inline-block;
  padding-block: 0.7rem;
  color: #67e8f9;
  font-size: 0.92rem;
  font-weight: 800;
}

.alt-hero-link:hover {
  text-decoration: underline;
  text-underline-offset: 4px;
}

/* ---------- Body / article typography (mirrors the blog editorial prose) ---------- */
.alt-body {
  padding: 1rem 0 4.5rem;
}

.alt-section {
  margin-top: 4rem;
}

.alt-section:first-child {
  margin-top: 2.5rem;
}

.alt-section h2 {
  margin-top: 0.55rem;
  margin-bottom: 1.1rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2.5rem;
  color: #020617;
  font-size: clamp(1.45rem, 2.6vw, 1.8rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.18;
  scroll-margin-top: 6rem;
}

.alt-section:first-child h2 {
  border-top: 0;
  padding-top: 0;
}

.alt-section p {
  margin: 1.05rem 0;
  color: #3f4c63;
  font-size: 1.02rem;
  line-height: 1.8;
}

.alt-section a {
  color: #0f172a;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: #cbd5e1;
  text-underline-offset: 0.22em;
  transition: color 150ms ease, text-decoration-color 150ms ease;
}

.alt-section a:hover {
  color: #1ba7ce;
  text-decoration-color: #1ba7ce;
}

.alt-section strong {
  color: #0f172a;
  font-weight: 800;
}

.alt-section ul {
  display: grid;
  gap: 0.55rem;
  margin: 1.1rem 0;
  padding-left: 1.3rem;
}

.alt-section ul li {
  color: #3f4c63;
  font-size: 0.98rem;
  line-height: 1.7;
}

.alt-section ul li::marker {
  color: #94a3b8;
}

.alt-subhead {
  margin: 2.25rem 0 0.75rem;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 850;
}

.alt-section :is(a, button, summary):focus-visible,
.alt-hero a:focus-visible,
.alt-sticky a:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

/* Quick answer callout: flat tint panel, no gradient */
.alt-quick {
  border: 1px solid rgb(14 116 144 / 0.25);
  border-radius: 10px;
  background: #f0f9fc;
  padding: 1.35rem 1.45rem;
}

.alt-quick p {
  color: #164e57;
  font-weight: 550;
}

.alt-quick p:first-child {
  margin-top: 0;
}

.alt-quick p:last-child {
  margin-bottom: 0;
}

/* Events timeline: hairline rows, tabular date rail */
.alt-events {
  display: grid;
  margin: 1.5rem 0 0;
  padding: 0;
  list-style: none;
}

.alt-events li {
  display: grid;
  gap: 0.2rem;
  border-top: 1px solid #e8eef4;
  padding: 1.1rem 0;
}

.alt-events li:first-child {
  border-top: 0;
  padding-top: 0.25rem;
}

.alt-event-date {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
}

.alt-events h3 {
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 800;
}

.alt-events p {
  margin: 0.35rem 0 0;
  font-size: 0.92rem;
}

@media (min-width: 700px) {
  .alt-events li {
    grid-template-columns: 8.5rem minmax(0, 1fr);
    gap: 1.25rem;
  }

  .alt-event-date {
    padding-top: 0.2rem;
  }
}

/* Quotes: blog blockquote idiom + attribution footer */
.alt-quote {
  margin: 1.4rem 0;
  border-left: 3px solid #1ba7ce;
  padding-left: 1.15rem;
  color: #24344d;
}

.alt-quote p {
  margin: 0;
  font-size: 0.98rem;
  font-style: italic;
}

.alt-quote footer {
  margin-top: 0.5rem;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.6;
}

.alt-quote footer a {
  font-size: 0.8rem;
}

/* ---------- Comparison table (pricing-matrix idiom: shell, sticky first column) ---------- */
.alt-section-head p {
  max-width: 46rem;
}

.alt-table-shell {
  position: relative;
  margin-top: 1.5rem;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 22px 60px rgb(15 23 42 / 0.07);
  -webkit-overflow-scrolling: touch;
  /* Subtle edge shadows that appear only where more content is scrollable */
  background-image:
    linear-gradient(to right, #ffffff, #ffffff),
    linear-gradient(to right, #ffffff, #ffffff),
    linear-gradient(to right, rgb(15 23 42 / 0.12), rgb(15 23 42 / 0)),
    linear-gradient(to left, rgb(15 23 42 / 0.12), rgb(15 23 42 / 0));
  background-position: left center, right center, left center, right center;
  background-repeat: no-repeat;
  background-size:
    1.5rem 100%,
    1.5rem 100%,
    0.8rem 100%,
    0.8rem 100%;
  background-attachment: local, local, scroll, scroll;
}

.alt-table {
  width: 100%;
  min-width: 66rem;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}

.alt-table thead th {
  background: #f8fafc;
  padding: 0.85rem 0.9rem;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-align: left;
  text-transform: uppercase;
  vertical-align: bottom;
  border-bottom: 1px solid #e2e8f0;
  min-width: 6.5rem;
}

.alt-table tbody th,
.alt-table tbody td {
  border-top: 1px solid #eef0f3;
  padding: 0.85rem 0.9rem;
  vertical-align: top;
  text-align: left;
  line-height: 1.55;
}

.alt-table tbody tr:first-child th,
.alt-table tbody tr:first-child td {
  border-top: 0;
}

.alt-table tbody td {
  color: #3f4c63;
  font-weight: 550;
}

.alt-col-tool {
  position: sticky;
  inset-inline-start: 0;
  z-index: 10;
  min-width: 11.5rem;
  max-width: 11.5rem;
  background: #ffffff;
  box-shadow: inset -1px 0 0 #e2e8f0;
}

.alt-table tbody th.alt-col-tool {
  color: #0f172a;
  font-weight: 800;
}

.alt-table thead th.alt-col-tool {
  background: #f8fafc;
  z-index: 20;
}

.alt-tool-pricing-link {
  /* 44px tap target on phones (was 28px). */
  display: flex;
  min-height: 44px;
  align-items: center;
  margin-top: 0;
  font-size: 0.72rem;
  font-weight: 700;
}

.alt-tag {
  display: inline-block;
  margin-left: 0.4rem;
  border-radius: 4px;
  background: #e2e8f0;
  color: #475569;
  padding: 0.1rem 0.5rem;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: 0.1em;
}

.alt-tag-us {
  background: rgb(27 167 206 / 0.14);
  color: #155e75;
}

/* Baseline (the competitor) row: quiet gray; our row: quiet cyan tint */
.alt-row-baseline th,
.alt-row-baseline td {
  background: #fafafa;
}

.alt-row-baseline th.alt-col-tool {
  background: #fafafa;
}

.alt-row-us th,
.alt-row-us td {
  background: #f0f9fc;
}

.alt-row-us th.alt-col-tool {
  background: #f0f9fc;
}

.alt-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  background: #0e7490;
  color: #ffffff;
}

.alt-check svg {
  display: block;
  width: 0.7rem;
  height: 0.7rem;
}

.alt-dash {
  color: #94a3b8;
}

.alt-dash-link {
  text-decoration: none;
}

.alt-dash-link:hover .alt-dash {
  color: #0e7490;
}

.alt-table-note-row {
  padding: 0.9rem 1rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 0.78rem;
  line-height: 1.6;
}

.alt-table-note {
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.6;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ---------- Per-tool sections ---------- */
.alt-tool {
  margin-top: 2.75rem;
  border-top: 1px solid #e8eef4;
  padding-top: 1.75rem;
}

.alt-tool:first-of-type {
  margin-top: 1.5rem;
}

.alt-tool h3 {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  align-items: baseline;
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 880;
  scroll-margin-top: 6rem;
}

.alt-tool-site {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
}

.alt-tool-best {
  margin-top: 0.75rem;
}

.alt-tool-grid {
  display: grid;
  gap: 0.5rem 2.5rem;
  margin-top: 0.5rem;
}

@media (min-width: 760px) {
  .alt-tool-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.alt-tool-label {
  margin: 0.75rem 0 0;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.alt-tool-skip {
  border-top: 1px solid #e8eef4;
  padding-top: 1rem;
}

.alt-tool-ctas {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.8rem 1.2rem;
  margin: 1.4rem 0 0.5rem;
}

/* A trial button with its own trial line under it. Groups share a row where two fit and stack on phones; the
   pricing link takes its own line after them. */
.alt-trial {
  display: flex;
  flex: 1 1 15rem;
  flex-direction: column;
  align-items: flex-start;
  max-width: 22rem;
}

.alt-trial :deep(.trial-disclosure) {
  margin-top: 0.5rem;
}

.alt-cta-center .alt-trial {
  align-items: center;
}

.alt-tool-ctas .alt-inline-cta,
.alt-cta-row .alt-inline-cta {
  flex-basis: 100%;
}

.alt-cta-center .alt-inline-cta {
  text-align: center;
}

.alt-inline-cta {
  /* Taller tap area (44px+) without moving the text: vertical padding on an inline link does not change the line box. */
  padding-block: 0.7rem;
  color: #0e7490;
  font-size: 0.92rem;
  font-weight: 800;
}

/* ---------- Stay / steps / faq ---------- */
.alt-stay li::marker {
  color: #1ba7ce;
}

.alt-steps {
  display: grid;
  gap: 0.9rem;
  margin: 1.5rem 0;
  padding: 0;
  list-style: none;
}

@media (min-width: 760px) {
  .alt-steps {
    grid-template-columns: 1fr 1fr;
  }
}

.alt-steps li {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 1.3rem 1.4rem;
}

.alt-step-num {
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.alt-steps h3 {
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 800;
}

.alt-steps p {
  margin: 0.4rem 0 0;
  font-size: 0.87rem;
  line-height: 1.6;
}

.alt-faq div {
  border-top: 1px solid #e5e7eb;
  padding: 1.15rem 0;
}

.alt-faq dt {
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 800;
}

.alt-faq dd {
  margin-top: 0.5rem;
  color: #4b5563;
  font-size: 0.92rem;
  line-height: 1.7;
  /* Answers can cite a bare source URL, which is wider than a phone column. */
  overflow-wrap: break-word;
}

/* Translated editions carry long compounds (German, Finnish) that are wider than a phone column on their own. They wrap
   rather than hyphenate: auto hyphenation split brand names (Works-pace369, Honey-Book).
   `anywhere` because these blocks sit in grid and flex items, which only shrink past a long word with it. */
.alt :is(h1, h2, h3, p, td, th, li, dt, dd):not(:lang(en)) { hyphens: manual; overflow-wrap: anywhere; }

/* ---------- Final CTA: flat tint panel ---------- */
.alt-final {
  border: 1px solid #d7ebf2;
  border-radius: 10px;
  background: #f7fcfe;
  padding: 2.5rem 1.5rem;
  text-align: center;
}

@media (min-width: 700px) {
  .alt-final {
    padding: 2.75rem 2.25rem;
  }
}

.alt-final-logo {
  width: 2.6rem;
  height: 2.6rem;
  margin-inline: auto;
  color: #0e7490;
}

.alt-final h2 {
  margin: 1rem auto 0;
  max-width: 34rem;
  border-top: 0;
  padding-top: 0;
  font-size: clamp(1.5rem, 3vw, 2.1rem);
}

.alt-final > p {
  margin: 0.8rem auto 0;
  max-width: 34rem;
  font-size: 0.95rem;
}

.alt-cta-center {
  justify-content: center;
}

/* ---------- Related links ---------- */
.alt-related {
  display: grid;
  gap: 0;
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
}

.alt-related a {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1.25rem;
  border-top: 1px solid #e8eef4;
  padding: 0.95rem 0;
  text-decoration: none;
}

.alt-related li:last-child a {
  border-bottom: 1px solid #e8eef4;
}

.alt-related a strong {
  display: block;
  font-size: 0.95rem;
}

.alt-related a small {
  display: block;
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.5;
}

.alt-related-arrow {
  color: #0e7490;
  flex: none;
}

/* ---------- Sources ---------- */
.alt-sources-title {
  font-size: 1.05rem;
}

.alt-sources {
  display: grid;
  gap: 0.45rem;
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;
}

.alt-sources a {
  font-size: 0.82rem;
  font-weight: 650;
}

/* ---------- Mobile sticky CTA ---------- */
.alt-sticky {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem;
  border-top: 1px solid #e5e7eb;
  /* Opaque: at 94% white the hero's white text showed through behind the trial line. */
  background: #ffffff;
  padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
}

.alt-sticky strong {
  display: block;
  color: #111827;
  font-size: 0.82rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alt-sticky :deep(.trial-disclosure.compact) {
  /* The trial line beside the button: TrialDisclosure's approved short form (B1.1), never under 12px. */
  margin: 0;
  line-height: 1.35;
}

.alt-sticky-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem; /* 44px tap target */
  border-radius: 6px;
  background: #0e7490;
  color: #ffffff;
  padding: 0.5rem 1.05rem;
  font-size: 0.84rem;
  font-weight: 800;
  white-space: nowrap;
}

/* On phones (below 480px) the bar keeps the trial line and drops the title, which repeats the H1. */
@media (max-width: 479px) {
  .alt-sticky strong {
    display: none;
  }
}

@media (min-width: 820px) {
  .alt-sticky {
    display: none;
  }
}

@media (max-width: 819px) {
  .alt {
    padding-bottom: 4.25rem;
  }
}

.alt-sticky > div { min-width: 0; }
/* Below 480px the short trial line beside the button ran 4-7 lines at 125% text. Stack it: the button
   full width, the trial line directly under it on one full-width row (B1.1), and room for the taller bar. */
@media (max-width: 479px) {
  .alt-sticky { flex-wrap: wrap; gap: 0.45rem; }
  .alt-sticky > div { display: contents; }
  .alt-sticky-btn { order: 1; flex: 1 1 100%; }
  .alt-sticky :deep(.trial-disclosure.compact) { order: 2; flex: 1 1 100%; text-align: center; }
  .alt { padding-bottom: 7.5rem; }
}
/* Pricing beside the trial button, as in PhoneStickyCta (Q088 board, 2026-11-01). Both 44px+ tall. */
.alt-sticky-actions { display: flex; flex-shrink: 0; gap: 0.5rem; }
.alt-sticky-pricing { display: inline-flex; align-items: center; justify-content: center; min-height: 2.75rem; border: 1px solid #cbd5e1; border-radius: 6px; color: #111827; padding: 0.5rem 1rem; font-size: 0.84rem; font-weight: 700; text-align: center; line-height: 1.15; }
@media (max-width: 479px) {
  .alt-sticky > .alt-sticky-actions { order: 1; flex: 1 1 100%; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 0.6rem; }
  .alt-sticky-btn, .alt-sticky-pricing { min-height: 2.875rem; }
}
.alt-sticky { transition: transform 0.2s ease, visibility 0.2s; }
.alt-sticky.is-off { transform: translateY(110%); visibility: hidden; }
@media (prefers-reduced-motion: reduce) { .alt-sticky { transition: none; } }
.alt-table-shell:focus-visible { outline: 2px solid #0e7490; outline-offset: 3px; }

.alt-section .alt-btn:not(.alt-btn-outline),
.alt-section .alt-btn:not(.alt-btn-outline):hover { color: #ffffff; }
</style>

<style>
/* Dark mode: the same neutrals and cyan accent as the /switch pages (html.dark .sw). The hero is
   already dark in both modes, so only the article, table, steps, FAQ and sticky bar change. */
html.dark .alt {
  --alt-ink: #fafafa; --alt-ink2: #c2c2c2; --alt-ink3: #9a9a9a; --alt-edge: #2c2c2c;
  --alt-canvas: #1a1a1a; --alt-page: #121212; --alt-raised: #202020; --alt-accent: #67e8f9;
  --alt-tint: rgb(103 232 249 / 0.07);
  background: var(--alt-page);
  color: var(--alt-ink);
}
html.dark .alt :is(.alt-section h2, .alt-section strong, .alt-subhead, .alt-events h3, .alt-table tbody th.alt-col-tool,
  .alt-tool h3, .alt-steps h3, .alt-faq dt, .alt-sticky strong, .alt-btn-outline) { color: var(--alt-ink); }
html.dark .alt :is(.alt-section p, .alt-section ul li, .alt-table tbody td, .alt-faq dd, .alt-quote) { color: var(--alt-ink2); }
html.dark .alt :is(.alt-quote footer, .alt-table thead th, .alt-table-note, .alt-tool-site, .alt-tool-label,
  .alt-related a small, .alt-section ul li::marker, .alt-dash, .alt-table-note-row) { color: var(--alt-ink3); }
html.dark .alt :is(.alt-section a, .alt-event-date, .alt-inline-cta, .alt-step-num, .alt-final-logo, .alt-related-arrow,
  .alt-dash-link:hover .alt-dash, .alt-section a:hover, .alt-btn-outline:hover) { color: var(--alt-accent); }
html.dark .alt .alt-section h2 { border-top-color: var(--alt-edge); }
html.dark .alt .alt-sticky-pricing { border-color: var(--alt-edge); color: var(--alt-ink); }
html.dark .alt :is(.alt-events li, .alt-tool, .alt-tool-skip, .alt-related a, .alt-faq div) { border-top-color: var(--alt-edge); }
html.dark .alt .alt-related li:last-child a { border-bottom-color: var(--alt-edge); }
html.dark .alt .alt-btn-outline { border-color: #3a3a3a; }
html.dark .alt .alt-btn-outline:hover { border-color: var(--alt-accent); background: var(--alt-tint); }
html.dark .alt :is(.alt-btn:not(.alt-btn-outline), .alt-check, .alt-sticky-btn) { border-color: var(--alt-accent); background: var(--alt-accent); color: #0b1220; }
html.dark .alt .alt-section .alt-btn:not(.alt-btn-outline) { color: #0b1220; }
html.dark .alt .alt-quick { border-color: rgb(103 232 249 / 0.25); background: var(--alt-tint); }
html.dark .alt .alt-quick p { color: var(--alt-ink); }
html.dark .alt .alt-quote { border-left-color: var(--alt-accent); }
html.dark .alt .alt-table-shell {
  border-color: var(--alt-edge);
  background-color: var(--alt-canvas);
  background-image:
    linear-gradient(to right, var(--alt-canvas), var(--alt-canvas)),
    linear-gradient(to right, var(--alt-canvas), var(--alt-canvas)),
    linear-gradient(to right, rgb(0 0 0 / 0.45), rgb(0 0 0 / 0)),
    linear-gradient(to left, rgb(0 0 0 / 0.45), rgb(0 0 0 / 0));
  box-shadow: none;
}
html.dark .alt :is(.alt-table thead th, .alt-table thead th.alt-col-tool, .alt-table-note-row) { background: var(--alt-raised); border-color: var(--alt-edge); }
html.dark .alt :is(.alt-table tbody th, .alt-table tbody td) { border-top-color: var(--alt-edge); }
html.dark .alt .alt-col-tool { background: var(--alt-canvas); box-shadow: inset -1px 0 0 var(--alt-edge); }
html.dark .alt :is(.alt-row-baseline th, .alt-row-baseline td, .alt-row-baseline th.alt-col-tool) { background: #161616; }
html.dark .alt :is(.alt-row-us th, .alt-row-us td, .alt-row-us th.alt-col-tool) { background: #10222a; }
html.dark .alt .alt-tag { background: #2a2a2a; color: var(--alt-ink2); }
html.dark .alt .alt-tag-us { background: rgb(103 232 249 / 0.14); color: var(--alt-accent); }
html.dark .alt .alt-stay li::marker { color: var(--alt-accent); }
html.dark .alt .alt-steps li { border-color: var(--alt-edge); background: var(--alt-canvas); }
html.dark .alt .alt-final { border-color: rgb(103 232 249 / 0.2); background: #0f1a1f; }
html.dark .alt .alt-sticky { border-top-color: var(--alt-edge); background: var(--alt-canvas); }
/* Every trial line here passes on-light (#475569); on the dark surfaces it takes the page's secondary ink. */
html.dark .alt .trial-disclosure.light { color: var(--alt-ink2); }
html.dark .alt :is(.alt-section :is(a, button, summary):focus-visible, .alt-table-shell:focus-visible) { outline-color: var(--alt-accent); }
</style>
